import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UpdateTranslation {
  title: string;
  short_description: string;
  full_description: string;
}

export interface AppUpdate {
  id: string;
  title: string;
  short_description: string | null;
  full_description: string | null;
  version: string | null;
  update_type: string;
  priority: string;
  status: string;
  display_popup: boolean;
  display_banner: boolean;
  display_notification: boolean;
  display_badge: boolean;
  badge_target_path: string | null;
  action_label: string | null;
  action_url: string | null;
  scheduled_at: string | null;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  translations?: Record<string, Partial<UpdateTranslation>> | null;
}

/**
 * Resolve title/short/full for an update in a given locale.
 * Order: requested locale → English → base columns on the row.
 */
export function resolveUpdateContent(u: AppUpdate, locale: string): { title: string; short_description: string; full_description: string } {
  const t = u.translations || {};
  const pick = (loc: string) => t[loc];
  const chosen = pick(locale) || pick("en") || null;
  return {
    title: chosen?.title || u.title,
    short_description: chosen?.short_description ?? u.short_description ?? "",
    full_description: chosen?.full_description ?? u.full_description ?? "",
  };
}

export interface UpdateView {
  id: string;
  update_id: string;
  user_id: string;
  seen_at: string;
  clicked_at: string | null;
  dismissed_at: string | null;
  display_method: string | null;
}

export function usePublishedUpdates() {
  return useQuery({
    queryKey: ["app-updates-published"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("app_updates")
        .select("*")
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data as AppUpdate[];
    },
  });
}

export function useAllUpdates() {
  return useQuery({
    queryKey: ["app-updates-all"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("app_updates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as AppUpdate[];
    },
  });
}

export function useUpdateViews(updateId?: string) {
  return useQuery({
    queryKey: ["update-views", updateId],
    queryFn: async () => {
      let q = (supabase as any).from("update_views").select("*");
      if (updateId) q = q.eq("update_id", updateId);
      const { data, error } = await q;
      if (error) throw error;
      return data as UpdateView[];
    },
    enabled: !!updateId,
  });
}

export function useMyUpdateViews() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my-update-views", user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("update_views")
        .select("*")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data as UpdateView[];
    },
    enabled: !!user,
  });
}

export function useMarkUpdateSeen() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ updateId, method }: { updateId: string; method: string }) => {
      if (!user) return;
      const { error } = await (supabase as any)
        .from("update_views")
        .upsert({
          update_id: updateId,
          user_id: user.id,
          display_method: method,
          seen_at: new Date().toISOString(),
        }, { onConflict: "update_id,user_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-update-views"] }),
  });
}

export function useMarkUpdateClicked() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updateId: string) => {
      if (!user) return;
      const { error } = await (supabase as any)
        .from("update_views")
        .upsert({
          update_id: updateId,
          user_id: user.id,
          clicked_at: new Date().toISOString(),
          seen_at: new Date().toISOString(),
        }, { onConflict: "update_id,user_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-update-views"] }),
  });
}

export function useMarkUpdateDismissed() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updateId: string) => {
      if (!user) return;
      const { error } = await (supabase as any)
        .from("update_views")
        .upsert({
          update_id: updateId,
          user_id: user.id,
          dismissed_at: new Date().toISOString(),
          seen_at: new Date().toISOString(),
        }, { onConflict: "update_id,user_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-update-views"] }),
  });
}

export function useSaveUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (update: Partial<AppUpdate> & { id?: string }) => {
      if (update.id) {
        const { id, created_at, ...rest } = update;
        const { error } = await (supabase as any)
          .from("app_updates")
          .update(rest)
          .eq("id", id);
        if (error) throw error;
      } else {
        const { id, ...rest } = update;
        const { error } = await (supabase as any)
          .from("app_updates")
          .insert(rest);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-updates-all"] });
      qc.invalidateQueries({ queryKey: ["app-updates-published"] });
    },
  });
}

export function useDeleteUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from("app_updates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-updates-all"] });
      qc.invalidateQueries({ queryKey: ["app-updates-published"] });
    },
  });
}

export function useUpdateStats(updateId: string) {
  return useQuery({
    queryKey: ["update-stats", updateId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("update_views")
        .select("*")
        .eq("update_id", updateId);
      if (error) throw error;
      const views = data as UpdateView[];
      return {
        total_seen: views.length,
        total_clicked: views.filter(v => v.clicked_at).length,
        total_dismissed: views.filter(v => v.dismissed_at).length,
      };
    },
    enabled: !!updateId,
  });
}

export const UPDATE_TYPE_LABELS: Record<string, { label: string; emoji: string; color: string }> = {
  new_feature: { label: "Ny funktion", emoji: "🚀", color: "bg-emerald-500/10 text-emerald-600" },
  improvement: { label: "Förbättring", emoji: "✨", color: "bg-blue-500/10 text-blue-600" },
  bugfix: { label: "Buggfix", emoji: "🔧", color: "bg-amber-500/10 text-amber-600" },
  important: { label: "Viktigt", emoji: "⚠️", color: "bg-red-500/10 text-red-600" },
  maintenance: { label: "Drift", emoji: "🔄", color: "bg-gray-500/10 text-gray-600" },
};

export const PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
  high: { label: "Hög", color: "bg-red-500/10 text-red-600" },
  medium: { label: "Medium", color: "bg-amber-500/10 text-amber-600" },
  low: { label: "Låg", color: "bg-gray-500/10 text-gray-600" },
};
